import { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { TabView, TabPanel } from 'primereact/tabview';
import { getRowClassName } from '../utils/common';
import EnrichmentResultTab from './tabs/EnrichmentResultTab';
import MappingTab from './tabs/MappingTab';
import SourceDataTab from './tabs/SourceDataTab';
import MappedResultTab from './tabs/MappedResultTab';
import PrioritiesSidebar from './PrioritiesSidebar';

export const MappingForm = forwardRef(({ data, onMappingChange, onDownload, onDownloadAbu, onSavePriorities, onReload, onApplyDefaultPriorities }, ref) => {
    const mappingObj = useMemo(
        () => (data && data.mapping ? { ...data.mapping } : {}),
        [data]
    );
    const [currentMapping, setCurrentMapping] = useState(mappingObj);
    const [priorities, setPriorities] = useState(data?.priorities || {});
    const [prioVisible, setPrioVisible] = useState(false);
    const [prioColumn, setPrioColumn] = useState(null);
    const [prioEdit, setPrioEdit] = useState({});
    const [prioGroupVin, setPrioGroupVin] = useState(null);
    const [isMappingChanged, setIsMappingChanged] = useState(false);
    const [prioSaving, setPrioSaving] = useState(false);

    useEffect(() => {
        const derived = { ...mappingObj };

        // Registration date -> Registration year (same logic as handleMappingChange)
        const regDate = derived['Registration date'];
        if (regDate) {
            const yearFormula = `=year(${regDate})`;
            if (derived['Registration year'] !== yearFormula) {
                derived['Registration year'] = yearFormula;
            }
        }

        // UPE Net EUR -> UPE Net EUR (rounded) (same logic as handleMappingChange)
        const upeNet = derived['UPE Net EUR'];
        if (upeNet) {
            const roundFormula = `=round(${upeNet})`;
            if (derived['UPE Net EUR (rounded)'] !== roundFormula) {
                derived['UPE Net EUR (rounded)'] = roundFormula;
            }
        }

        setCurrentMapping(derived);
    }, [mappingObj]);

    useEffect(() => {
        setPriorities(data?.priorities || {});
    }, [data?.priorities]);

    useImperativeHandle(ref, () => ({
        getMapping: () => ({ ...currentMapping })
    }));

    const mappedColumnsSet = useMemo(
        () => new Set(Object.values(currentMapping).filter(Boolean)),
        [currentMapping]
    );

    const mapping = useMemo(() => (data && data.mapping ? data.mapping : {}), [data]);
    const preview = useMemo(() => (data && data.preview ? data.preview : {}), [data]);
    const grouping = useMemo(() => (data && data.grouping ? data.grouping : []), [data]);

    const previewColumns = useMemo(() => (preview ? Object.keys(preview) : []), [preview]);
    const mappingFields = useMemo(() => (mapping ? Object.keys(mapping) : []), [mapping]);

    const dropdownOptions = useMemo(() => {
        const base = [{ label: '(none)', value: null }].concat(
            previewColumns.map(col => ({ label: col, value: col }))
        );
        // Also add any formula values already in the mapping (e.g. =year(Field1))
        // so the Dropdown always has a matching option for its current value.
        const baseValues = new Set(base.map(o => o.value));
        const formulaOptions = [...new Set(
            Object.values(currentMapping)
                .filter(v => typeof v === 'string' && v.startsWith('=') && !baseValues.has(v))
        )].map(v => ({ label: v, value: v }));
        return base.concat(formulaOptions);
    }, [previewColumns, currentMapping]);

    const previewRows = useMemo(() => {
        const rows = [];
        if (preview && previewColumns.length > 0) {
            const rowLen = (preview[previewColumns[0]] || []).length;
            for (let i = 0; i < rowLen; i++) {
                const row = {};
                previewColumns.forEach(col => {
                    row[col] = preview[col][i] ?? '';
                });
                rows.push(row);
            }
        }
        return rows;
    }, [preview, previewColumns]);

    const resultColumns = mappingFields;

    const resultRows = useMemo(() => {
        const rows = [];
        if (previewRows.length > 0) {
            for (let i = 0; i < previewRows.length; i++) {
                const row = {};
                resultColumns.forEach(field => {
                    const mappedCol = currentMapping[field];
                    if (!mappedCol) { row[field] = ''; return; }

                    if (typeof mappedCol === 'string' && mappedCol.startsWith('=')) {
                        // Evaluate formula against raw preview data
                        const yearMatch = mappedCol.match(/^=year\((.+)\)$/i);
                        if (yearMatch) {
                            const raw = preview[yearMatch[1]]?.[i] ?? '';
                            if (raw) { const d = new Date(raw); const yr = d.getFullYear(); row[field] = yr > 2010 && yr < 2030 ? String(yr) : ''; }
                            else row[field] = '';
                            return;
                        }
                        if (/^=fakevin\(\)$/i.test(mappedCol)) {
                            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                            row[field] = 'FAKE' + Array.from({ length: 13 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
                            return;
                        }
                        const fakeVinMatch = mappedCol.match(/^=fakevinifempty\((.+)\)$/i);
                        if (fakeVinMatch) {
                            const raw = preview[fakeVinMatch[1]]?.[i] ?? '';
                            if (raw) { row[field] = raw; return; }
                            const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
                            row[field] = 'FAKE' + Array.from({ length: 13 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
                            return;
                        }
                        const roundMatch = mappedCol.match(/^=round\((.+)\)$/i);
                        if (roundMatch) {
                            const raw = preview[roundMatch[1]]?.[i] ?? '';
                            const num = parseFloat(raw);
                            row[field] = isNaN(num) ? '' : String(Math.ceil(num / 1000) * 1000);
                            return;
                        }
                        const constMatch = mappedCol.match(/^=const\("(.*)"\)$/i);
                        if (constMatch) { row[field] = constMatch[1]; return; }
                        const vatMatch = mappedCol.match(/^=(addvat|substractvat)\((.+),\s*([\d.]+)\)$/i);
                        if (vatMatch) {
                            const [, fn, srcCol, percentStr] = vatMatch;
                            const raw = preview[srcCol]?.[i] ?? '';
                            const num = parseFloat(raw);
                            const percent = parseFloat(percentStr);
                            if (raw && !isNaN(num)) {
                                row[field] = (fn.toLowerCase() === 'addvat'
                                    ? num * (1 + percent / 100)
                                    : num / (1 + percent / 100)).toFixed(2);
                            } else {
                                row[field] = '';
                            }
                            return;
                        }
                        const concatMatch = mappedCol.match(/^=concat\((.+)\)$/i);
                        if (concatMatch) {
                            const parts = concatMatch[1].match(/(?:[^,"]+|"[^"]*")+/g)?.map(s => s.trim()) ?? [];
                            const sep = parts.length > 0 ? parts[0].replace(/^"|"$/g, '') : '';
                            const fields = parts.slice(1);
                            row[field] = fields.map(f => preview[f]?.[i] ?? '').join(sep);
                            return;
                        }
                        const replaceMatch = mappedCol.match(/^=replace\((.+)\)$/i);
                        if (replaceMatch) {
                            const parts = replaceMatch[1].match(/(?:[^,"]+|"[^"]*")+/g)?.map(s => s.trim()) ?? [];
                            let val = preview[parts[0]]?.[i] ?? '';
                            for (let p = 1; p + 1 < parts.length; p += 2)
                                val = val.split(parts[p].replace(/^"|"$/g, '')).join(parts[p + 1].replace(/^"|"$/g, ''));
                            row[field] = val;
                            return;
                        }
                        row[field] = '';
                        return;
                    }

                    row[field] = preview[mappedCol] ? preview[mappedCol][i] : '';
                });
                row.GroupVin = row.VIN;
                row.Source = '1';
                rows.push(row);
            }
        }

        return rows;
    }, [previewRows, resultColumns, currentMapping, preview]);

    const notMappedFieldsSet = useMemo(
        () => new Set(
            Object.entries(currentMapping)
                .filter(([field, mappedCol]) => !mappedCol)
                .map(([field]) => field)
        ),
        [currentMapping]
    );

    const handleMappingChange = (field, value) => {
        const updated = { ...currentMapping, [field]: value === undefined ? null : value };

        // Registration date -> Registration year
        if (field === 'Registration date') {
            if (value) {
                const yearFormula = `=year(${value})`;
                if (updated['Registration year'] !== yearFormula) {
                    updated['Registration year'] = yearFormula;
                }
            } else {
                const prev = currentMapping['Registration date'];
                const prevFormula = prev ? `=year(${prev})` : null;
                if (prevFormula && updated['Registration year'] === prevFormula) {
                    updated['Registration year'] = null;
                }
            }
        }

        // UPE Net EUR -> UPE Net EUR (rounded)
        if (field === 'UPE Net EUR') {
            if (value) {
                const roundFormula = `=round(${value})`;
                if (updated['UPE Net EUR (rounded)'] !== roundFormula) {
                    updated['UPE Net EUR (rounded)'] = roundFormula;
                }
            } else {
                const prev = currentMapping['UPE Net EUR'];
                const prevFormula = prev ? `=round(${prev})` : null;
                if (prevFormula && updated['UPE Net EUR (rounded)'] === prevFormula) {
                    updated['UPE Net EUR (rounded)'] = null;
                }
            }
        }

        setCurrentMapping(updated);
        setIsMappingChanged(true);
        if (onMappingChange) onMappingChange(updated);
    };

    const handleDownload = async (columnList) => {
        if (!onDownload) return;

        try {
            await onDownload(currentMapping, data.preview, false, columnList);
        } finally {

        }
    };

    const handleDownloadAbu = onDownloadAbu
        ? async () => {
            await onDownloadAbu(currentMapping, data.preview);
          }
        : null;

    const styleForValue = (val, extra = {}) => {
        if (typeof val !== 'string') return extra;
        if (val.startsWith('✓')) return { ...extra, color: 'green', fontWeight: 600 };
        if (val.startsWith('✗')) return { ...extra, color: '#c00', fontWeight: 600 };
        if (val.startsWith('?')) return { ...extra, color: '#FF8C00' };
        return extra;
    };

    const fullCustomCell = ({ value, rowData, col }) => {
        const isFormula = typeof value === 'string' && value.startsWith('=');
        let displayValue = isFormula ? value.slice(1) : value;
        let baseStyle = isFormula ? { backgroundColor: '#d4edda' } : {};

        if (typeof displayValue === 'string' && displayValue.startsWith(':')) {
            displayValue = displayValue.slice(1);
            baseStyle = { ...baseStyle, backgroundColor: '#d4edda', fontWeight: 600 };
        }

        const style = styleForValue(displayValue, baseStyle);

        const onOpenPrio = () => {
            setPrioColumn(col);
            setPrioGroupVin(rowData?.GroupVin ?? null); // <-- capture GroupVin of clicked cell
            const current = priorities?.[col] || {};
            const norm = Object.fromEntries(
                Object.entries(current).map(([k, v]) => [k, String(v ?? '')])
            );
            setPrioEdit(norm);
            setPrioVisible(true);
        };

        // Ensure the whole cell is clickable, including empty cells
        const content = (displayValue === undefined || displayValue === null || displayValue === '')
            ? '\u00A0'
            : displayValue;

        const isDisabled = (col === 'FromSource' || col === 'VIN' || col === 'Equipments info');
        const containerStyle = {
            cursor: isDisabled ? 'default' : 'pointer',
            width: '100%',
            minHeight: 24,
            display: 'flex',
            alignItems: 'center',
            padding: 0,
        };

        if (isDisabled) {
            // Non-clickable cell for FromSource
            return (
                <div style={containerStyle} title="Source indicator">
                    <span style={style}>{content}</span>
                </div>
            );
        }

        // Clickable for all other columns
        return (
            <div
                role="button"
                tabIndex={0}
                onClick={onOpenPrio}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') ? onOpenPrio() : null}
                style={containerStyle}
                title={`Edit priorities for "${col}"`}
            >
                <span style={style}>{content}</span>
            </div>
        );
    };

    const updatePrioValue = (source, newVal) => {
        setPrioEdit(prev => ({ ...prev, [source]: newVal }));
    };

    const handleSavePriorities = async (applyToColumns = []) => {
        if (!prioColumn || prioSaving) return;

        setPrioSaving(true);

        const cleaned = Object.fromEntries(
            Object.entries(prioEdit).map(([k, v]) => {
                const n = Number(v);
                return [k, Number.isFinite(n) && n > 0 ? n : 1];
            })
        );

        // Unique list: current column + checked columns
        const cols = Array.from(new Set([prioColumn, ...applyToColumns].filter(Boolean)));

        // Update local state for all selected columns
        setPriorities(prev => {
            const next = { ...prev };
            for (const col of cols) next[col] = cleaned;
            return next;
        });

        try {
            // Persist all updates first
            if (typeof onSavePriorities === 'function') {
                await Promise.all(cols.map(col => onSavePriorities(col, cleaned)));
            }

            // Then, trigger one enrichment run
            if (typeof onReload === 'function') {
                await onReload(currentMapping, 'enrich-top');
            }
        } finally {
            setPrioSaving(false);
            setPrioVisible(false);
        }
    };

    const fullRows = useMemo(() => {
        if (!data || !data.allEnrichedRows || !data.allEnriched || !data.columns) return [];

        const allRows = data.allEnrichedRows.concat(data.allEnriched);

        let enriched = allRows.map(rowArr => {
            const rowObj = {};
            data.columns.forEach((col, idx) => {
                rowObj[col] = rowArr[idx];
            });
            return rowObj;
        });

        // Sort by GroupNo ascending (numeric), GroupVin ascending, Source descending
        enriched.sort((a, b) => {
            const groupNoA = Number(a.GroupNo ?? 0);
            const groupNoB = Number(b.GroupNo ?? 0);
            if (groupNoA < groupNoB) return -1;
            if (groupNoA > groupNoB) return 1;
            if (a.GroupVin < b.GroupVin) return -1;
            if (a.GroupVin > b.GroupVin) return 1;
            if (a.Source > b.Source) return -1;
            if (a.Source < b.Source) return 1;
            return 0;
        });

        return enriched;

    }, [data]);

    const fullResultColumns = useMemo(() => {
        if (!data || !data.columns) return [];

        const filtered = data.columns.filter(
            col => col !== 'GroupVin' && col !== 'Source' && col !== 'GroupNo'
        );

        const idx = filtered.indexOf('FromSource');
        if (idx > -1) {
            filtered.splice(idx, 1);
            filtered.unshift('FromSource');
        }
        return filtered;
    }, [data]);

    // Columns eligible for applying the same priorities (exclude non-editable)
    const prioAvailableColumns = useMemo(() => {
        const banned = new Set(['FromSource', 'VIN', 'Equipments info']);
        return (fullResultColumns || []).filter(c => !banned.has(c));
    }, [fullResultColumns]);

    // Build filtered rows for the sidebar based on the clicked cell's GroupVin
    const rowsForSidebar = useMemo(() => {
        if (!prioGroupVin) return [];
        return (fullRows || []).filter(r => r?.GroupVin === prioGroupVin);
    }, [fullRows, prioGroupVin]);

    // Add activeIndex to state
    const initialTabCount = 4 + (fullRows?.length > 0 ? 1 : 0);
    const [activeIndex, setActiveIndex] = useState(initialTabCount - 2);

    return (
        <>
            <PrioritiesSidebar
                visible={prioVisible}
                onHide={() => (!prioSaving && setPrioVisible(false))}
                prioColumn={prioColumn}
                priorities={priorities}
                prioEdit={prioEdit}
                setPrioEdit={setPrioEdit}
                updatePrioValue={updatePrioValue}
                onSave={handleSavePriorities}
                rows={rowsForSidebar}
                availableColumns={prioAvailableColumns}
                isSaving={prioSaving}
            />

            <TabView 
                activeIndex={activeIndex}
                onTabChange={(e) => {
                    setActiveIndex(e.index);
                    if (e.index === 3 && isMappingChanged) { // Assuming 'Full Result' is the 4th tab (index 3)
                        setIsMappingChanged(false);
                        if (onReload) {
                            onReload(currentMapping);
                        }
                    }
                }}>
                <TabPanel header="Mapping">
                    <MappingTab
                        mappingFields={mappingFields}
                        currentMapping={currentMapping}
                        handleMappingChange={handleMappingChange}
                        dropdownOptions={dropdownOptions}
                        preview={preview}
                    />
                </TabPanel>

                <TabPanel
                    header={
                        fullRows?.length > 0
                            ? `Source Data (${previewRows.length})`
                            : `Supplier Data (top ${previewRows.length})`
                    }
                >
                    <SourceDataTab
                        previewRows={previewRows}
                        previewColumns={previewColumns}
                        mappedColumnsSet={mappedColumnsSet}
                        getRowClassName={getRowClassName}
                    />
                </TabPanel>

                <TabPanel
                    header={
                        fullRows?.length > 0
                            ? `Mapped Sample Rows (${resultRows.length})`
                            : `Mapped Supplier Data (top ${resultRows.length})`
                    }>
                    <MappedResultTab
                        rows={resultRows}
                        columns={resultColumns}
                        notMappedFieldsSet={notMappedFieldsSet}
                        getRowClassName={getRowClassName}
                        handleDownload={handleDownload}
                        showDownload={!(fullRows?.length > 0)}
                    />
                </TabPanel>
                <TabPanel header={`Full Result (preview)`}
                    visible={fullRows?.length > 0}>
                    <EnrichmentResultTab
                        handleDownload={handleDownload}
                        handleDownloadAbu={handleDownloadAbu}
                        enrichedRows={fullRows}
                        grouping={grouping}
                        resultColumns={fullResultColumns}
                        customCell={fullCustomCell}
                        onApplyDefaultPriorities={onApplyDefaultPriorities} // <-- pass down
                    />
                </TabPanel>
            </TabView>
   </>);
});

export default MappingForm;
